// BoardResolutionsTab.tsx — Sprint 15. Resolutions table with the
// inline cast-vote / close-tally action row, plus a "N open" alert
// banner above the table.
import React from 'react';
import {
  Alert, Badge, Button, Card, Popconfirm, Progress, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { BoardResolution } from '../../types';
import type { VoteStatus } from './boardTypes';

const { Text } = Typography;

export interface BoardResolutionsTabProps {
  resolutions: BoardResolution[];
  onAdd:       () => void;
  onEdit:      (resolution: BoardResolution) => void;
  onDelete:    (id: string) => void;
  onCastVote:  (resolution: BoardResolution, vote: 'FOR' | 'AGAINST' | 'ABSTAIN') => void;
  onClose:     (resolution: BoardResolution) => void;
}

export const BoardResolutionsTab: React.FC<BoardResolutionsTabProps> = ({
  resolutions, onAdd, onEdit, onDelete, onCastVote, onClose,
}) => {
  const openCount = resolutions.filter(v => v.status === 'open').length;

  const columns = [
    {
      title: 'Resolution', dataIndex: 'voteNumber', key: 'num',
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'Category', dataIndex: 'category', key: 'cat',
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: VoteStatus, r: BoardResolution) => (
        <Space>
          <Badge
            status={v === 'open' ? 'processing' : 'default'}
            text={v === 'open' ? 'Open' : 'Closed'}
          />
          {r.result && (
            <Tag color={r.result === 'passed' ? 'green' : r.result === 'failed' ? 'red' : 'orange'}>
              {r.result.toUpperCase()}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Votes (For / Against / Abstain)', key: 'votes',
      render: (_: any, r: BoardResolution) => {
        const total = r.votesFor + r.votesAgainst + r.votesAbstain;
        const pct   = total > 0 ? Math.round((r.votesFor / total) * 100) : 0;
        return (
          <Space direction="vertical" size={0} style={{ width: 140 }}>
            <Text style={{ fontSize: 12 }}>
              {r.votesFor} / {r.votesAgainst} / {r.votesAbstain}
            </Text>
            <Progress
              percent={pct} size="small"
              strokeColor={pct >= 50 ? '#52c41a' : '#ff4d4f'}
              showInfo={false}
            />
          </Space>
        );
      },
    },
    {
      title: 'Close Date', dataIndex: 'closeDate', key: 'close',
      render: (v: string) => dayjs(v).format('MMM D, YYYY'),
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: BoardResolution) => (
        <Space wrap size={4}>
          {r.status === 'open' && (
            <>
              <Tooltip title="Cast vote FOR">
                <Button
                  size="small" type="primary"
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => onCastVote(r, 'FOR')}
                >
                  For
                </Button>
              </Tooltip>
              <Tooltip title="Cast vote AGAINST">
                <Button size="small" danger onClick={() => onCastVote(r, 'AGAINST')}>Against</Button>
              </Tooltip>
              <Tooltip title="Abstain">
                <Button size="small" onClick={() => onCastVote(r, 'ABSTAIN')}>Abstain</Button>
              </Tooltip>
              <Tooltip title="Close & Tally">
                <Button
                  size="small" type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => onClose(r)}
                >
                  Close
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
          </Tooltip>
          <Popconfirm title="Delete?" onConfirm={() => onDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>New Resolution</Button>}>
      {openCount > 0 && (
        <Alert
          message={`${openCount} active resolution(s) currently open for voting`}
          type="info" showIcon style={{ marginBottom: 16 }}
        />
      )}
      <Table
        columns={columns}
        dataSource={resolutions}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 900 }}
      />
    </Card>
  );
};
